// AppLayout — AppBar + optionele Drawer (op cluster-wizard-pagina's) + Content.
// Mobile-first: drawer collapsed op <md; op desktop permanent zichtbaar.

import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
    Button,
    Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import { useAuth } from '@rvdh/react-auth-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OfflineBanner from './OfflineBanner.jsx';
import useIsAdmin from '../hooks/useIsAdmin.js';

const DRAWER_WIDTH = 300;

export default function AppLayout({ children, sidebar, title }) {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const isAdmin = useIsAdmin();

    const handleLogout = () => {
        try { window.localStorage.removeItem('zbs:lastSession'); } catch { /* geen opslag */ }
        logout();
        navigate('/login', { replace: true });
    };

    const hasSidebar = Boolean(sidebar);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppBar position="sticky" color="primary">
                <Toolbar>
                    {hasSidebar && !isDesktop && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setMobileOpen(v => !v)}
                            sx={{ mr: 1 }}
                            aria-label="Open menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box
                        onClick={() => navigate('/')}
                        sx={{
                            flexGrow: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1.5, md: 2.5 },
                            minWidth: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src="/logo/zbs-icon.png"
                            alt="Z Battery Solutions"
                            sx={{
                                height: { xs: 48, sm: 72 },
                                width: { xs: 48, sm: 72 },
                                display: 'block',
                                flexShrink: 0,
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1 }}>
                            <Typography
                                component="div"
                                sx={{
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.35rem' },
                                    letterSpacing: '0.04em',
                                    color: '#ffffff',
                                    lineHeight: 1.05,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Z BATTERY SOLUTIONS
                            </Typography>
                            <Typography
                                component="div"
                                sx={{
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.72rem', sm: '0.85rem' },
                                    letterSpacing: { xs: '0.02em', sm: '0.06em' },
                                    color: 'secondary.light',
                                    mt: 0.4,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Projectdossier: Vooropnames
                            </Typography>
                        </Box>
                        {title && (
                            <>
                                <Box
                                    sx={{
                                        display: { xs: 'none', md: 'block' },
                                        width: '1px',
                                        alignSelf: 'stretch',
                                        my: 1.5,
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        flexShrink: 0,
                                        ml: 2,
                                    }}
                                />
                                <Typography
                                    component="div"
                                    noWrap
                                    sx={{
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '1.1rem',
                                        letterSpacing: '0.015em',
                                        color: 'rgba(255,255,255,0.92)',
                                        display: { xs: 'none', md: 'block' },
                                    }}
                                >
                                    {title}
                                </Typography>
                            </>
                        )}
                    </Box>
                    {isAdmin && (
                        <Tooltip title="Accounts beheren">
                            <Button
                                color="inherit"
                                onClick={() => navigate('/accounts')}
                                startIcon={<GroupIcon />}
                                aria-label="Accounts"
                                sx={{
                                    mr: 1,
                                    flexShrink: 0,
                                    textTransform: 'none',
                                    minWidth: { xs: 44, sm: 64 },
                                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                                }}
                            >
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Accounts</Box>
                            </Button>
                        </Tooltip>
                    )}
                    {user && (
                        <Tooltip title="Wachtwoord wijzigen">
                            <Button
                                color="inherit"
                                onClick={() => navigate('/wachtwoord')}
                                sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' }, textTransform: 'none', opacity: 0.9 }}
                            >
                                {typeof user === 'string' ? user : (user.username || user.name || '')}
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        color="inherit"
                        onClick={handleLogout}
                        startIcon={<LogoutIcon />}
                        aria-label={t('nav.logout')}
                        sx={{
                            flexShrink: 0,
                            minWidth: { xs: 44, sm: 64 },
                            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            {t('nav.logout')}
                        </Box>
                    </Button>
                </Toolbar>
            </AppBar>
            <OfflineBanner />

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {hasSidebar && (
                    <>
                        {isDesktop ? (
                            <Drawer
                                variant="permanent"
                                sx={{
                                    width: DRAWER_WIDTH,
                                    flexShrink: 0,
                                    '& .MuiDrawer-paper': {
                                        width: DRAWER_WIDTH,
                                        boxSizing: 'border-box',
                                        position: 'sticky',
                                        top: 96, // AppBar-hoogte, zodat sidebar onder header blijft
                                        alignSelf: 'flex-start',
                                        height: 'calc(100vh - 96px)',
                                        overflowY: 'auto',
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                    },
                                }}
                            >
                                {sidebar}
                            </Drawer>
                        ) : (
                            <Drawer
                                variant="temporary"
                                open={mobileOpen}
                                onClose={() => setMobileOpen(false)}
                                ModalProps={{ keepMounted: true }}
                                sx={{
                                    '& .MuiDrawer-paper': {
                                        width: DRAWER_WIDTH,
                                        boxSizing: 'border-box',
                                    },
                                }}
                            >
                                <div onClick={() => setMobileOpen(false)}>
                                    {sidebar}
                                </div>
                            </Drawer>
                        )}
                    </>
                )}
                <Box
                    component="main"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        p: { xs: 2, md: 3 },
                        maxWidth: '100%',
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
}
